/**
 * FeatureCard Molecule Component
 * Large icon + title + bullet list for feature highlights
 * Supports separate dark/light icon images that swap with theme
 */

import React, { useState, useEffect } from "react";
import { Image } from "@/components/atoms/Image";
import { Heading } from "@/components/atoms/Heading";
import { Text } from "@/components/atoms/Text";
import { FeatureCardBlok } from "@/types";

export interface FeatureCardProps extends FeatureCardBlok {
  className?: string;
}

export const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  icon_light,
  title,
  description,
  features,
  className = "",
}) => {
  // Track current theme to swap images
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  // Get icon - handle both string and asset types
  const darkSrc = icon
    ? typeof icon === "string"
      ? icon
      : (icon as any).filename || ""
    : "";
  const lightSrc = icon_light?.filename || darkSrc;
  const iconSrc = isDark ? darkSrc : lightSrc;
  const iconAlt =
    icon && typeof icon !== "string" ? (icon as any).alt : "Feature Icon";

  // Convert features string (multiline text) to array
  const featureList = features
    ? features.split("\n").filter((f) => f.trim())
    : [];

  return (
    <div
      className={`flex flex-col items-start gap-4 md:gap-6 lg:gap-8 p-3 sm:p-4 md:p-6 lg:p-8 bg-theme-surface rounded-2xl md:rounded-3xl ${className}`}
    >
      {/* Large Icon/Image */}
      {iconSrc && (
        <div className="w-full h-32 sm:h-40 md:h-56 lg:h-64 flex items-center justify-center rounded-2xl">
          <Image
            src={iconSrc}
            alt={iconAlt}
            className="w-full h-full object-contain"
          />
        </div>
      )}

      {/* Title */}
      <Heading
        level="h3"
        className="text-black dark:text-primary uppercase tracking-wide"
      >
        {title}
      </Heading>

      {/* Bullet List */}
      {featureList.length > 0 && (
        <ul className="w-full space-y-3 md:space-y-4">
          {featureList.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <span className="flex-shrink-0 w-1 h-1 mt-1.5 rounded-full bg-black dark:bg-white" />
              <span className="font-space-mono text-sm leading-tight text-black dark:text-white">
                {feature}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Description (fallback if no features) */}
      {featureList.length === 0 && description && (
        <span className="font-space-mono text-sm leading-tight text-black dark:text-white">
          {description}
        </span>
      )}
    </div>
  );
};

export default FeatureCard;
